import type { Customer, JournalEntry, Product, Sale, ShopSettings, UnitDef } from '@k2pos/core';
import { authed } from './client';

/**
 * Every call the app makes, in one place.
 *
 * The server is the source of truth. A mutation posts what happened and gets
 * the shop's whole state back, so the device never computes a balance or a
 * stock figure of its own — which is what stops two tills from disagreeing.
 */

/** Recent-activity row on Home. */
export interface ActivityEntry {
  id: string;
  customerName: string;
  detail: string;
  kind: 'credit' | 'paid';
  amount: number;
}

/** Everything the screens read. Mirrors the store's field names one for one. */
export interface ShopState {
  /** Bumped on every write. Polling sends it back to ask "anything new?". */
  version: number;

  catalog: Product[];
  /** Products the shop has stopped selling. Kept out of `catalog` so no screen
   *  has to remember to filter them; old bills still name them. */
  retired: Product[];
  customers: Customer[];
  activity: ActivityEntry[];
  orders: Sale[];
  journal: JournalEntry[];

  salesToday: number;
  receivedToday: number;
  billsToday: number;
  collectedToday: number;

  currentDay: string;
  journalStartedAt: string;

  shop: ShopSettings;
  units: UnitDef[];
  categories: string[];
  expenseCategories: string[];
  suppliers: string[];
}

interface Mutation {
  state: ShopState;
  /** The id of whatever was created, where the caller needs it. */
  result?: string;
}

async function mutate(path: string, body: unknown): Promise<Mutation> {
  return (await authed(path, { method: 'POST', body: JSON.stringify(body) })) as Mutation;
}

// --------------------------------------------------------------------------- //
// Reading
// --------------------------------------------------------------------------- //

/**
 * The shop's state.
 *
 * Pass the version already held and the server answers `null` when nothing has
 * moved — which is what makes polling every few seconds cost almost nothing
 * while the shop is quiet.
 */
export async function fetchState(since?: number): Promise<ShopState | null> {
  const query = since === undefined ? '' : `?since=${since}`;
  const body = (await authed(`/api/v1/state${query}`)) as
    | { unchanged: true }
    | { state: ShopState };
  return 'unchanged' in body ? null : body.state;
}

export async function fetchOrders(): Promise<Sale[]> {
  const body = (await authed('/api/v1/orders')) as { orders: Sale[] };
  return body.orders;
}

// --------------------------------------------------------------------------- //
// Money
// --------------------------------------------------------------------------- //

export interface SellInput {
  lines: { productId: string; name: string; unit: string; price: number; qty: number }[];
  payMode: 'cash' | 'credit';
  currency?: string;
  /** LAK per one unit of `currency`, snapshotted onto the bill. */
  rate?: number;
  customerId?: string;
}

export const sell = (input: SellInput) => mutate('/api/v1/sales', input);

export const receivePayment = (customerId: string, amount: number) =>
  mutate('/api/v1/payments', { customerId, amount });

export interface PurchaseInput {
  supplier: string;
  lines: { productId: string; name: string; unit: string; qty: number; unitCost: number }[];
  paid: boolean;
  cash: boolean;
  /** ISO-8601 *with an offset*. The server refuses a naive one: Laos is UTC+7,
   *  and reading a bare timestamp as UTC files it on the wrong day. */
  at?: string;
}

export const recordPurchase = (input: PurchaseInput) => mutate('/api/v1/purchases', input);

export interface ExpenseInput {
  category: string;
  amount: number;
  cash: boolean;
  note?: string;
  /** ISO-8601 with an offset; see PurchaseInput. */
  at?: string;
}

export const addExpense = (input: ExpenseInput) => mutate('/api/v1/expenses', input);

/** Writes the opposite entry. Never a delete — the ledger corrects. */
export const reverseExpense = (id: string) => mutate(`/api/v1/expenses/${id}/reverse`, {});

export const closeDay = (counted: number | null) => mutate('/api/v1/day-closes', { counted });

// --------------------------------------------------------------------------- //
// Catalogue, customers, stock
// --------------------------------------------------------------------------- //

export interface ProductInput {
  name: string;
  cat: string;
  unit: string;
  price: number;
  cost?: number;
  /** Omitted means the product is not stock tracked — never send 0 for that. */
  opening?: number;
  lowStockAt?: number;
  masterId?: string;
  masterVersion?: number;
}

export const addProduct = (input: ProductInput) => mutate('/api/v1/products', input);

export interface ProductPatch {
  name?: string;
  cat?: string;
  /** Explicit `null` turns the low-stock warning off; omit to leave it alone. */
  lowStockAt?: number | null;
  /** Add-only. An absent unit is left in place, never removed. */
  units?: { unit: string; price: number; perBase?: number }[];
}

export async function updateProduct(id: string, patch: ProductPatch): Promise<Mutation> {
  return (await authed(`/api/v1/products/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })) as Mutation;
}

/** Stops the shop selling something. Never a delete — old bills name it. */
export const retireProduct = (id: string, restore = false) =>
  mutate(`/api/v1/products/${id}/retire${restore ? '?restore=1' : ''}`, {});

export const addCustomer = (input: { name: string; phone?: string; limit?: number }) =>
  mutate('/api/v1/customers', input);

/**
 * Moves stock without moving money.
 *
 * `counted` is what is on the shelf; the server works out the difference under
 * a lock, so a sale landing between reading and writing cannot corrupt the
 * count. `delta` is for the case where the change is what is known — four bags
 * broke — and the total is not.
 */
export const countStock = (productId: string, counted: number, reason = 'count') =>
  mutate('/api/v1/stock-adjustments', { productId, counted, reason });

export const adjustStock = (productId: string, delta: number, reason: string) =>
  mutate('/api/v1/stock-adjustments', { productId, delta, reason });

// --------------------------------------------------------------------------- //
// Configuration
// --------------------------------------------------------------------------- //

export async function updateSettings(patch: Partial<ShopSettings>): Promise<Mutation> {
  return (await authed('/api/v1/settings', {
    method: 'PUT',
    body: JSON.stringify(patch),
  })) as Mutation;
}

/**
 * Every product picture the shop has, keyed by product id.
 *
 * One request for the whole catalogue rather than one per tile: a shop has tens
 * of products, and thirty requests fired as a grid paints is worse for its
 * connection than one answer that can be held for the session. Products with no
 * picture are absent.
 */
export async function fetchProductImages(): Promise<Record<string, string>> {
  const body = (await authed('/api/v1/products/images')) as {
    images: Record<string, string>;
  };
  return body.images;
}

/** `null` removes the picture. */
export async function setProductImage(id: string, image: string | null): Promise<Mutation> {
  return (await authed(`/api/v1/products/${encodeURIComponent(id)}/image`, {
    method: 'PUT',
    body: JSON.stringify({ image }),
  })) as Mutation;
}

/**
 * The shop's bank QR.
 *
 * Its own call, not part of the settings patch: the image is tens of kilobytes
 * and the settings endpoint answers with the whole state. Fetched only when
 * `shop.qrRev` moves.
 */
export async function fetchShopQr(): Promise<string | null> {
  const body = (await authed('/api/v1/settings/qr')) as { image: string | null; rev: number };
  return body.image;
}

/** `null` removes it. A QR is configuration, so replacing one is not a deletion. */
export async function setShopQr(image: string | null): Promise<Mutation> {
  return (await authed('/api/v1/settings/qr', {
    method: 'PUT',
    body: JSON.stringify({ image }),
  })) as Mutation;
}

/** Add-only, all four kinds. See `catalogue.ts` for why nothing is removed. */
export const addVocabulary = (
  kind: 'unit' | 'category' | 'expense' | 'supplier',
  name: string,
  step?: number,
) => mutate(`/api/v1/vocabulary/${kind}`, step === undefined ? { name } : { name, step });

// --------------------------------------------------------------------------- //
// The master catalogue
// --------------------------------------------------------------------------- //

export interface MasterGroup {
  cat: string;
  items: (ProductInput & { masterId: string; masterVersion: number })[];
}

export async function fetchMasterProducts(): Promise<MasterGroup[]> {
  const body = (await authed('/api/v1/master/products')) as { groups: MasterGroup[] };
  return body.groups;
}

export interface MeResponse {
  shop: { id: string; code: string; name: string; status: string };
  device?: { id: string; name: string };
  user?: { id: string; displayName: string; role: string | null };
}

export const fetchMe = () => authed('/api/v1/me') as Promise<MeResponse>;
